import React, { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import CommonSelectField from '../../common/form/CommonSelectField';
import { CircularProgress } from '@material-ui/core';
import CommonButton from '../../common/CommonButton';
import { fetchBackups } from '../../../actions/report-actions';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  BACKGROUND_WHITE,
  BLACK_COLOR,
  LIGHT_GREY,
} from '../../../const/color-const';
import { STANDARD_INPUT_WIDTH } from '../../../const/chart-const';
import { lighten } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { generateUniqueCustomerOptions, transformToMachineOptions } from '../../../utils/report-utils';

const styles = makeStyles(theme => ({
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  formContent: {
    width: '100%',
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    '& .commonSelectField, & .MultiSelectField': {
      width: 260,
      margin: theme.spacing(1, 1, 1, 1),
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      [theme.breakpoints.down('sm')]: {
        width: STANDARD_INPUT_WIDTH,
      },
    },
    '& > div:nth-child(2)': {
      '& .commonSelectField': {
        marginRight: theme.spacing(3),
      },
    },
  },
  button: {
    width: 300,
    backgroundColor: LIGHT_GREY,
  },
  sectionWrapper: {
    width: '100%',
    maxWidth: 1600,
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '& button': {
      width: '100%',
      maxWidth: 200,
    },
  },
  description: {
    margin: theme.spacing(1),
  },
  loadingBar: {
    display: 'flex',
    alignSelf: 'center',
    marginTop: theme.spacing(2),
  },
}));

const BackupForm = ({ machines, fetchBackups }) => {
  const machinesOptions = transformToMachineOptions(machines);
  const uniqueCustomers = generateUniqueCustomerOptions(machines);
  const initCustomerValue = uniqueCustomers[0]?.value;
  const [availableMachines, setAvailableMachines] = useState(machinesOptions);
  const formikRef = useRef();
  const classes = styles();
  
  const filterCustomers = (companyName) => (companyName ? machinesOptions.filter(machine => machine.customerName === companyName) : []);
  
  const updateMachineIDs = (machineData) => {
    formikRef.current.setFieldValue('machineIDs', [machineData[0]?.value]);
    setAvailableMachines(machineData);
  };
  
  useEffect(() => {
    if (formikRef.current) {
      updateMachineIDs(filterCustomers(initCustomerValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        customerNames: initCustomerValue,
      }}
      onSubmit={(values, setSubmitting) => {
        fetchBackups(values.customerNames, values.machineIDs, setSubmitting);
      }}>
      {({ isSubmitting, isValid }) => {
        return (
          <Form className={classes.formWrapper}>
            <div className={classes.formContent}>
              <Field
                wide
                atLeastOneElement
                name="customerNames"
                label={<FormattedMessage
                  id="field.company.names"
                  defaultMessage="Customer"
                />}
                options={uniqueCustomers}
                component={CommonSelectField}
                extraHandleChangeAction={(values) => {
                  const data = filterCustomers(values);
                  updateMachineIDs(data);
                }}
              />
              <Field
                wide
                atLeastOneElement
                name="machineIDs"
                label={<FormattedMessage
                  id="field.cash.register"
                  defaultMessage="Cash Register"
                />}
                options={availableMachines}
                component={CommonSelectField}
              />
            </div>
            
            <div className={classes.sectionWrapper}>
              <CommonButton
                type="submit"
                isAnimated
                size="large"
                disabled={!!isSubmitting || !isValid}
                label={<FormattedMessage id="search.backups" defaultMessage="Search for backups" />}
              />
              <div className={classes.description}>
                <FormattedMessage id="download.backup.files"
                                  defaultMessage="Below you can download your backup files." />
              </div>
            </div>
            
            {isSubmitting && <CircularProgress className={classes.loadingBar} />}
          </Form>
        );
      }}
    </Formik>
  );
};

BackupForm.propTypes = {
  machines: PropTypes.array,
  fetchBackups: PropTypes.func,
};

const decorate = compose(
  connect(
    null,
    dispatch => bindActionCreators({ fetchBackups }, dispatch)),
);

export default decorate(BackupForm);



